import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { insertIf } from '@backmarket/utils/collection/insertIf'

import type { FileValue } from '../models/review-form'

export function usePostOrderReviewAttachments() {
  const $httpFetch = useHttpFetch()
  const { openErrorToast } = useTheToast()
  const logger = useLogger()

  async function uploadAttachments({
    attachmentFiles,
    orderlineId,
    authToken,
  }: {
    attachmentFiles: File[]
    orderlineId: string | string[]
    authToken?: string
  }): Promise<FileValue[]> {
    const uploadFile = async (file: File) => {
      try {
        // Step 1: Preflight
        const { data: preflightData } = await $httpFetch(
          '/reviews/sales-order-item/:uuid/review/attachment',
          {
            method: 'POST',
            pathParams: { uuid: orderlineId.toString() },
            body: {
              upload: {
                original_filename: file.name,
                original_filesize: file.size,
                original_lastmod: file.lastModified,
                original_mimetype: file.type,
              },
            },
            query: insertIf(!!authToken, { authToken }),
          },
        )

        if (!preflightData?.form?.url || !preflightData?.handle) {
          throw new Error(`Invalid preflight data for file: ${file.name}`)
        }

        // Step 2: Upload to S3
        const formData = new FormData()
        Object.entries(preflightData.form.fields).forEach(([key, val]) =>
          formData.append(key, val),
        )
        formData.append('file', file)

        const uploadData = await fetch(preflightData.form.url, {
          method: 'POST',
          body: formData,
        })

        if (!uploadData.ok) {
          throw new Error(`Failed to upload file: ${file.name} on S3`)
        }

        // Step 3: Acknowledge
        const result = await $httpFetch(
          '/reviews/sales-order-item/:uuid/review/attachment/ack',
          {
            method: 'POST',
            pathParams: {
              uuid: orderlineId.toString(),
            },
            body: {
              upload: {
                handle: preflightData.handle,
                status: 'success',
                uploadInfo: {
                  headers: Object.fromEntries(uploadData.headers),
                },
              },
            },
            query: insertIf(!!authToken, { authToken }),
          },
        )

        return { fileName: file.name, uploadId: result.data.handle }
      } catch (err) {
        logger.error('[ReviewCollection] POST Attachment', {
          error: err as Error,
          owners: ['bot-squad-content-front'],
        })

        openErrorToast()

        return { fileName: '', uploadId: '' }
      }
    }

    return await Promise.all(
      attachmentFiles.map((attachment) => uploadFile(attachment)),
    )
  }

  return uploadAttachments
}
